<template>
  <div class="ic">
    <div class="ic-head">
      <div class="ic-title">
        {{ texts.title }}
        <span class="ic-count">{{ items.length }}</span>
        <v-checkbox
          v-if="items.length > 1"
          :input-value="allSkipped"
          :indeterminate="someSkipped && !allSkipped"
          label="Пропустить все"
          hide-details
          dense
          class="ic-skip ic-skip-all"
          @change="skipAll($event)"
        />
      </div>
      <div class="ic-text">
        {{ texts.text }}
      </div>
    </div>

    <div class="ic-list">
      <div
        v-for="item in items"
        :key="item.group || item.key"
        class="ic-row"
        :class="{ done: decided(item) }"
      >
        <!-- Филиал не определён: одна группа = один префикс названия -->
        <template v-if="type === 'no_filial'">
          <div class="ic-name">
            {{ item.label || '(без названия)' }}
          </div>
          <div class="ic-sub">
            {{ zonesWord(item.keys.length) }}
            <span v-if="item.candidates && item.candidates.length">
              · похоже на: {{ item.candidates.map(filialName).join(', ') }}
            </span>
          </div>
          <div class="ic-controls">
            <v-autocomplete
              :value="res(item.keys[0]).filialId"
              :items="scopeFilials"
              :filter="filialFilter"
              item-value="id"
              item-text="name"
              placeholder="Выберите филиал"
              outlined
              dense
              hide-details
              class="ic-field"
              :disabled="!!res(item.keys[0]).skip"
              @change="setGroup(item.keys, { filialId: $event })"
            >
              <template v-slot:selection="{ item: f }">
                {{ f.address || f.name }}<span class="ic-city">, {{ f.city }}</span>
              </template>
              <template v-slot:item="{ item: f }">
                <global-filial-select-item :item="f" is-list-item />
              </template>
            </v-autocomplete>
            <v-checkbox
              :input-value="!!res(item.keys[0]).skip"
              label="Пропустить"
              hide-details
              dense
              class="ic-skip"
              @change="setGroup(item.keys, $event ? { skip: true } : {})"
            />
          </div>
        </template>

        <!-- Неизвестный цвет -->
        <template v-else-if="type === 'unknown_color'">
          <div class="ic-name">
            <span class="ic-swatch" :style="{ background: '#' + (item.rawFill || 'ccc') }" />
            {{ item.name || '(без названия)' }}
          </div>
          <div class="ic-controls">
            <v-btn-toggle
              :value="res(item.key).color"
              class="ic-toggle"
              @change="set(item.key, { color: $event })"
            >
              <v-btn
                v-for="c in colors"
                :key="c"
                :value="c"
                :disabled="!!res(item.key).skip"
                depressed
              >
                <span class="ic-dot" :style="{ background: zoneColors[c] }" />{{ colorNames[c] }}
              </v-btn>
            </v-btn-toggle>
            <v-checkbox
              :input-value="!!res(item.key).skip"
              label="Пропустить"
              hide-details
              dense
              class="ic-skip"
              @change="set(item.key, $event ? { skip: true } : {})"
            />
          </div>
        </template>

        <!-- Дубль: несколько полигонов указывают на одну зону -->
        <template v-else-if="type === 'duplicate'">
          <div class="ic-name">
            {{ row(item.keys[0]).name }}
          </div>
          <div
            v-for="(key, i) in item.keys"
            :key="key"
            class="ic-dup"
          >
            <span class="ic-sub">Полигон {{ i + 1 }} · {{ area(row(key).areaM2) }}</span>
            <v-btn-toggle
              :value="mode(key)"
              mandatory
              class="ic-toggle"
              @change="setDuplicate(item.keys, key, $event)"
            >
              <v-btn value="update" depressed>
                Обновит зону
              </v-btn>
              <v-btn value="asNew" depressed>
                Новая зона
              </v-btn>
              <v-btn value="skip" depressed>
                Пропустить
              </v-btn>
            </v-btn-toggle>
          </div>
          <div v-if="!decided(item)" class="ic-error">
            Зону может обновить только один полигон
          </div>
        </template>

        <!-- Имя занято -->
        <template v-else-if="type === 'name_taken'">
          <div class="ic-name">
            {{ item.name || '(без названия)' }}
          </div>
          <div class="ic-sub">
            {{ takenText(item) }}
          </div>
          <div class="ic-controls">
            <v-text-field
              :value="res(item.key).name"
              outlined
              dense
              hide-details="auto"
              placeholder="Новое имя зоны"
              class="ic-field"
              :disabled="!!res(item.key).skip"
              :error-messages="unresolvedText(item.key)"
              @input="set(item.key, { name: $event })"
            />
            <v-checkbox
              :input-value="!!res(item.key).skip"
              label="Пропустить"
              hide-details
              dense
              class="ic-skip"
              @change="set(item.key, $event ? { skip: true } : { name: item.suggestion })"
            />
          </div>
        </template>

        <!-- Зона другого филиала -->
        <template v-else-if="type === 'foreign_filial'">
          <div class="ic-name">
            {{ item.name }}
          </div>
          <div class="ic-sub">
            В файле: {{ filialName(item.tagFilialId) }} · зона принадлежит: {{ filialName(item.zoneFilialId) }}
          </div>
          <v-btn-toggle
            :value="res(item.key).skip ? 'skip' : res(item.key).keep ? 'keep' : null"
            class="ic-toggle"
            @change="set(item.key, $event === 'skip' ? { skip: true } : $event === 'keep' ? { keep: true } : {})"
          >
            <v-btn value="keep" depressed>
              Оставить в своём филиале
            </v-btn>
            <v-btn value="skip" depressed>
              Пропустить
            </v-btn>
          </v-btn-toggle>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
  import GlobalFilialSelectItem from '@/components/header/GlobalFilialSelectItem'
  import { ZONE_COLORS, ZONE_NAMES, ZONE_ORDER } from '@/constants/deliveryZones'
  import { zonesPlural } from '@/util/geo'
  import { CONFLICT_TEXTS, duplicateMode, isDecided } from './importResolutions'

  /**
   * Один подшаг конфликтов импорта (один тип). Решения - в v-model resolutions.
   */
  export default {
    name: 'ImportConflictsStep',
    components: { GlobalFilialSelectItem },
    props: {
      type: { type: String, required: true },
      items: { type: Array, default: () => [] },
      rowsByKey: { type: Object, default: () => ({}) },
      filials: { type: Array, default: () => [] },
      // филиалы, которые можно выбрать (охват импорта)
      scopeFilials: { type: Array, default: () => [] },
      value: { type: Object, default: () => ({}) },
      // ответ сервера: ключ → текст, почему решение не принято (например, имя снова занято)
      unresolved: { type: Object, default: () => ({}) },
    },
    data: () => ({
      colors: ZONE_ORDER,
      zoneColors: ZONE_COLORS,
      colorNames: ZONE_NAMES,
    }),
    computed: {
      texts () {
        return CONFLICT_TEXTS[this.type] || { title: '', text: '' }
      },
      // все ключи решений этого подшага: у групп (филиал, дубль) их несколько
      allKeys () {
        return this.items.flatMap((item) => item.keys || [item.key])
      },
      allSkipped () {
        return this.allKeys.length > 0 && this.allKeys.every((k) => this.res(k).skip)
      },
      someSkipped () {
        return this.allKeys.some((k) => this.res(k).skip)
      },
    },
    methods: {
      res (key) {
        return this.value[key] || {}
      },
      row (key) {
        return this.rowsByKey[key] || {}
      },
      mode (key) {
        return duplicateMode(this.value[key])
      },
      decided (item) {
        return isDecided(this.type, item, this.value)
      },
      set (key, resolution) {
        this.$emit('input', { ...this.value, [key]: resolution })
      },
      setGroup (keys, resolution) {
        const next = { ...this.value }
        keys.forEach((k) => {
          next[k] = { ...resolution }
        })
        this.$emit('input', next)
      },
      // «Пропустить все»: отметить пропуск у всех строк подшага; снятие возвращает решения
      // по умолчанию (как у снятого «Пропустить» в строке), дальше строки правятся вручную
      skipAll (on) {
        const next = { ...this.value }
        this.items.forEach((item) => {
          (item.keys || [item.key]).forEach((k) => {
            next[k] = on ? { skip: true } : this.defaultResolution(item)
          })
        })
        this.$emit('input', next)
      },
      defaultResolution (item) {
        return this.type === 'name_taken' ? { name: item.suggestion } : {}
      },
      // в группе дубля «обновит зону» может быть только у одного полигона
      setDuplicate (keys, key, mode) {
        const toRes = (m) => (m === 'skip' ? { skip: true } : m === 'asNew' ? { asNew: true } : {})
        const next = { ...this.value, [key]: toRes(mode) }
        if (mode === 'update') {
          keys.filter((k) => k !== key && duplicateMode(next[k]) === 'update').forEach((k) => {
            next[k] = { asNew: true }
          })
        }
        this.$emit('input', next)
      },
      filialName (id) {
        const f = this.filials.find((x) => x.id === id)
        return f ? f.address || f.name : '#' + id
      },
      filialFilter (item, query) {
        const q = (query || '').toLowerCase()
        return [item.name, item.address, item.city].some((s) => (s || '').toLowerCase().includes(q))
      },
      takenText (item) {
        if (item.takenBy === 'file') return 'Такое имя у другого полигона в файле'
        if (item.takenBy === null || item.takenBy === undefined) return item.name ? 'Имя уже занято' : 'У зоны нет имени'
        return 'Такое имя у другой зоны сети'
      },
      unresolvedText (key) {
        return this.unresolved[key] ? [this.unresolved[key]] : []
      },
      zonesWord (n) {
        return zonesPlural(n)
      },
      area (m2) {
        return m2 >= 100000 ? (m2 / 1e6).toFixed(1) + ' км²' : Math.round(m2).toLocaleString('ru') + ' м²'
      },
    },
  }
</script>

<style scoped>
.ic-title {
  font-size: 16px;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 8px;
}
.ic-count {
  font-size: 12px;
  font-weight: 500;
  background: #fdecea;
  color: #ed4543;
  border-radius: 10px;
  padding: 1px 8px;
}
.ic-text {
  margin-top: 4px;
  font-size: 13px;
  color: rgba(0, 0, 0, 0.6);
}
.ic-list {
  margin-top: 12px;
  max-height: 46vh;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.ic-row {
  border: 1px solid #e0e3e7;
  border-left: 3px solid #ed4543;
  border-radius: 8px;
  padding: 10px 12px;
}
.ic-row.done {
  border-left-color: #4caf50;
}
.ic-name {
  font-size: 14px;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 6px;
  word-break: break-word;
}
.ic-sub {
  margin-top: 2px;
  font-size: 12px;
  color: rgba(0, 0, 0, 0.55);
}
.ic-controls {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-top: 8px;
}
.ic-field {
  flex: 1;
}
.ic-field ::v-deep fieldset {
  border-radius: 8px;
}
.ic-skip {
  margin: 0;
  padding: 0;
  flex: 0 0 auto;
}
.ic-skip-all {
  margin-left: auto;
}
.ic-skip-all ::v-deep .v-label {
  font-size: 14px;
  font-weight: 400;
}
.ic-toggle {
  margin-top: 8px;
  border-radius: 8px !important;
}
.ic-toggle ::v-deep .v-btn {
  font-size: 13px;
  text-transform: none;
  letter-spacing: 0;
  height: 34px !important;
  background: #fff !important;
}
.ic-toggle ::v-deep .v-btn.v-btn--active {
  color: #ff9100 !important;
  background: rgba(255, 145, 1, 0.1) !important;
}
.ic-toggle ::v-deep .v-btn.v-btn--active::before {
  opacity: 0 !important;
}
.ic-dot,
.ic-swatch {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  display: inline-block;
  margin-right: 6px;
  flex: 0 0 12px;
}
.ic-swatch {
  border-radius: 3px;
}
.ic-dup {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-top: 4px;
}
.ic-dup .ic-toggle {
  margin-top: 0;
}
.ic-error {
  margin-top: 6px;
  font-size: 12px;
  color: #ed4543;
}
.ic-city {
  color: rgba(0, 0, 0, 0.5);
}
</style>
